import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { memo } from 'react';
import toast from 'react-hot-toast';
import { useUsersStore } from '../../store/useUsersStore';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';

const MIN_CONTENT = 20;
const MAX_CONTENT = 5000;
const DEFAULT_TARGET = 150;

const PLACEHOLDERS = [
  { token: '{{title}}', label: 'Job title' },
  { token: '{{portfolio_links}}', label: 'Portfolio Links' },
  { token: '{{job_skills}}', label: 'Job Skills' },
  { token: '{{matching_job_skills}}', label: 'Matching Job Skills' },
];

const countWords = (text = '') => {
  const s = String(text).trim();
  if (!s) return 0;
  return s.split(/\s+/).filter(Boolean).length;
};

const EXAMPLES = [
  `I am "Your Name" having 6+ years of experience in {{matching_job_skills}}. I have read the requirement and can do this project with perfection. I would like to connect with you in chat to discuss more about your project. Call client by name "{{client_name}}". Include my portfolio links {{portfolio_links}}.`,
  `We are a team of experienced developers in {{matching_job_skills}}. Do not include my name in proposal. We can do this project with perfection. Please connect in chat for further discussion. Use "{{client_name}}" in greetings. Include our portfolio links {{portfolio_links}}.`,
  `Our company name is "Your Company Name", founded 10 years ago, with expertise in {{matching_job_skills}}. We can do this project with perfection. Please connect in chat if you have questions. Call client as "Dear {{client_name}}". We are open to discuss budget & timeline. Include portfolio links {{portfolio_links}}.`,
];

const SimpleAddTemplateForm = ({ onSuccess = () => {}, onCancel = () => {}, selectedTemplate: propSelectedTemplate = null }) => {
  const { user: fbUser } = useFirebaseAuth();
  const addAiTemplate = useUsersStore((s) => s.addAiTemplate);
  const updateAiTemplate = useUsersStore((s) => s.updateAiTemplate);
  const loadAiTemplates = useUsersStore((s) => s.loadAiTemplates);
  const aiLoading = useUsersStore((s) => s.aiLoading);
  const aiError = useUsersStore((s) => s.aiError);
  const getSelectedUser = useUsersStore((s) => s.getSelectedUser);

  const storeSelectedTemplate = useUsersStore((s) => s.selectedTemplate || s.selectedAiTemplate || null);
  const selectedTemplate = propSelectedTemplate || storeSelectedTemplate;

  const selectedSubUser = getSelectedUser ? getSelectedUser() : null;
  const selectedSubUserId = selectedSubUser ? (selectedSubUser.document_id || selectedSubUser.sub_user_id || selectedSubUser.id) : null;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [targetWordCount, setTargetWordCount] = useState(DEFAULT_TARGET);
  const [portfolioLinks, setPortfolioLinks] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [copiedToken, setCopiedToken] = useState('');

  const [showExamples, setShowExamples] = useState(false);

  const textareaRef = useRef(null);

    const handleBack = useCallback(() => {
    try { if (typeof onCancel === 'function') onCancel(); } catch (e) { /* ignore */ }
  }, [onCancel]);

  // Prefill when editing (supports API shape: content, target_word_count, portfolio_links)
  useEffect(() => {
    if (selectedTemplate && typeof selectedTemplate === 'object') {
      setContent(selectedTemplate.content || selectedTemplate.body || selectedTemplate.text || '');
      const target = selectedTemplate.targetWordCount ?? selectedTemplate.target_word_count ?? selectedTemplate.targetWords ?? DEFAULT_TARGET;
      setTargetWordCount(Number(target) || DEFAULT_TARGET);
      setPortfolioLinks(selectedTemplate.portfolioLinks || selectedTemplate.portfolio_links || '');
      setErrors({});
    } else {
      resetForm();
    }
  }, [selectedTemplate]);
  
  useEffect(() => {
    const onAddClick = () => { textareaRef.current?.focus(); };
    document.addEventListener('ai:add-template', onAddClick);
    return () => document.removeEventListener('ai:add-template', onAddClick);
  }, []);

  const wordCount = useMemo(() => countWords(content), [content]);

  // Insert placeholder and copy to clipboard with feedback
  const insertAtCursor = useCallback(async (token) => {
    // Insert into textarea
    const el = textareaRef.current;
    if (!el) {
      setContent((prev) => (prev ? prev + ' ' + token : token));
    } else {
      const start = el.selectionStart ?? content.length;
      const end = el.selectionEnd ?? content.length;
      const before = content.slice(0, start);
      const after = content.slice(end);
      const next = `${before}${token}${after}`;
      setContent(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
    }

    // Copy to clipboard
    try {
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(token);
      } else {
        const tmp = document.createElement('textarea');
        tmp.value = token;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
      
      // Show "Copied!" feedback
      setCopiedToken(token);
      setTimeout(() => {
        setCopiedToken('');
      }, 2000);
    } catch {
      // ignore
    }
  }, [content]);

  const resetForm = useCallback(() => {
    setContent('');
    setTargetWordCount(DEFAULT_TARGET);
    setPortfolioLinks('');
    setErrors({});
    setCopiedToken('');
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!content.trim()) {
      newErrors.content = 'Content is required';
    } else if (content.trim().length < MIN_CONTENT) {
      newErrors.content = `Content must be at least ${MIN_CONTENT} characters`;
    } else if (content.length > MAX_CONTENT) {
      newErrors.content = `Content must be at most ${MAX_CONTENT} characters`;
    }

    if (!targetWordCount || Number(targetWordCount) < 10) {
      newErrors.targetWordCount = 'Target word count must be at least 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const applyExample = useCallback((exampleText) => {
    // replace content with example and focus textarea
    setContent(exampleText);
    setShowExamples(false);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [setShowExamples, textareaRef]);



  // const validate = () => {
  //   const newErrors = {};

  //   if (!content.trim()) {
  //     newErrors.content = 'Content is required';
  //   } else if (content.trim().length < MIN_CONTENT) {
  //     newErrors.content = `Content must be at least ${MIN_CONTENT} characters`;
  //   } else if (content.length > MAX_CONTENT) {
  //     newErrors.content = `Content must be at most ${MAX_CONTENT} characters`;
  //   }

  //   if (!targetWordCount || Number(targetWordCount) < 10) {
  //     newErrors.targetWordCount = 'Target word count must be at least 10';
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

//  const applyExample = useCallback((exampleText) => {
//     // replace content with example and focus textarea
//     setContent(exampleText);
//     setShowExamples(false);
//     requestAnimationFrame(() => textareaRef.current?.focus());
//   }, []);

  const handleSave = useCallback(async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!selectedSubUserId) {
      toast.error('Please select a sub-user before saving templates');
      return;
    }

    setSaving(true);
    const idToken = typeof fbUser?.getIdToken === 'function' ? await fbUser.getIdToken() : null;
    const payload = {
      content: content.trim(),
      targetWordCount: Number(targetWordCount) ,
      portfolioLinks: (portfolioLinks || '').trim(),
      skills: 'All skills',
      order: 0,
      status: 'active',
    };

    try {
      let saved;
      if (selectedTemplate?.id) {
        saved = await updateAiTemplate(selectedSubUserId, selectedTemplate.id, payload, idToken);
      } else {
        try {
          saved = await addAiTemplate(selectedSubUserId, payload, idToken);
        } catch (err) {
          const is400OneTemplate = err?.response?.status === 400 &&
            /one.*template|already exists|update or delete/i.test(err?.message || '');
          if (is400OneTemplate && typeof loadAiTemplates === 'function') {
            const list = await loadAiTemplates(selectedSubUserId, idToken);
            const existing = Array.isArray(list) && list.length > 0 ? list[0] : null;
            if (existing?.id) {
              saved = await updateAiTemplate(selectedSubUserId, existing.id, payload, idToken);
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }
      toast.success('Template saved');
      resetForm();
      onSuccess(saved);
    } catch (err) {
      console.error('Save template failed', err);
      toast.error(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }, [
    addAiTemplate, updateAiTemplate, loadAiTemplates,
    content, wordCount, targetWordCount, portfolioLinks,
    selectedSubUserId, selectedTemplate, resetForm, onSuccess, fbUser,
  ]);

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-white/40 backdrop-blur-md"
        onClick={handleCancel}
      />
      
      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl border-[1.5px] border-primary-blue shadow-2xl w-full max-w-md 2xl:max-w-[30rem] max-h-[90vh] overflow-y-auto scrollbar-hide">
    <style>{`
      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }
      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
    `}</style>

        {/* Header */}
        <div className="relative px-8 pt-6 pb-5">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            {selectedTemplate && selectedTemplate.id ? 'Edit AI Template' : 'Add AI Template'}
          </h2>
          <button
            onClick={handleCancel}
            className="absolute right-6 top-6 hover:opacity-70 transition-opacity"
            disabled={saving}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
              <path d="M8 8L16 16M16 8L8 16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSave} className="px-8 pb-6">
          {/* Placeholders Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Placeholders
            </label>
            <div className="grid grid-cols-3 gap-2">
              {PLACEHOLDERS.map((placeholder) => (
                <button
                  key={placeholder.token}
                  type="button"
                  onClick={() => insertAtCursor(placeholder.token)}
                  className={`px-3 py-2 bg-blue-50 text-primary-blue text-xs font-medium rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors ${
                    copiedToken === placeholder.token ? 'bg-green-50 border-green-300' : ''
                  }`}
                  disabled={saving}
                  title={`Click to insert and copy: ${placeholder.token}`}
                >
                  {copiedToken === placeholder.token ? 'Copied!' : placeholder.label}
                </button>
              ))}
            </div>
          </div>

          {/* Target Word Count */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Target Word Count
            </label>
            <input
              type="number"
              min={10}
              max={5000}
              value={targetWordCount}
              onChange={(e) => {
                setTargetWordCount(Math.max(10, Number(e.target.value || DEFAULT_TARGET)));
                setErrors({ ...errors, targetWordCount: '' });
              }}
              className={`w-full px-4 py-3 border-[1.5px] border-primary-blue rounded-lg focus:outline-none focus:ring-primary-blue focus:ring-1  transition-colors text-sm ${
                errors.targetWordCount ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={saving}
            />
            {errors.targetWordCount && (
              <p className="text-red-500 text-xs mt-1">{errors.targetWordCount}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Current: {wordCount} words</p>
          </div>


          {/* Content Field */}
         <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">
                Content
              </label>
              <button
                type="button"
                onClick={() => setShowExamples(true)}
                className="text-sm text-primary-blue underline hover:text-primary-blue/80"
              >
                Show examples
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setErrors({ ...errors, content: '' });
              }}
              placeholder="Enter your proposal content..."
              className={`w-full border-[1.5px] border-primary-blue  rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-primary-blue focus:border-2 transition-colors resize-vertical ${
                errors.content ? 'border-red-500' : 'border-gray-300'
              }`}
              rows={4}
              disabled={saving}
            />
            {errors.content && (
              <p className="text-red-500 text-xs mt-1">{errors.content}</p>
            )}
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span className="italic">You can enter your name, company name, relevant skills and experience.</span>
              <span>{content.length} characters • {wordCount} words</span>
            </div>
          </div>

           {showExamples && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6">
          <div className="absolute inset-0 bg-black opacity-40" onClick={() => setShowExamples(false)} />
          <div className="relative bg-white rounded-lg shadow-lg max-w-3xl w-full z-10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold">AI Content Examples</h4>
              <button className="text-sm text-gray-600" onClick={() => setShowExamples(false)}>Close</button>
            </div>

            <ul className="space-y-4">
              {EXAMPLES.map((ex, idx) => (
                <li key={idx} className="p-3 border rounded flex justify-between gap-4">
                  <div className="prose max-w-none text-sm">{ex}</div>
                  <div className="flex flex-col gap-2">
                    <button
                     type="button"
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                      onClick={() => applyExample(ex)}
                    >
                      Use this
                    </button>
                    {/* <button
                      type="button"
                      className="px-3 py-1 border rounded text-sm"
                      onClick={() => insertAtCursor('\n\n' + ex + '\n\n')}
                    >
                      Insert at cursor
                    </button> */}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}


          

          {/* Info Box */}
          <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-700 italic">
              DO NOT INCLUDE YOUR PORTFOLIO LINKS IN CONTENT. Instead use "Portfolio Links" placeholder and input links in the box below.
            </p>
          </div>

          {/* Portfolio Links */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Portfolio Links
            </label>
            <input
              value={portfolioLinks}
              onChange={(e) => setPortfolioLinks(e.target.value)}
              placeholder="Enter portfolio links separated by comma"
              className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary-blue  focus:border-2 transition-colors"
              disabled={saving}
            />
          </div>

          
          {/* Error Display */}
          {aiError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{aiError}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 py-2.5 bg-primary-blue text-white font-semibold rounded-lg hover:bg-primary-blue/70 transition-colors text-base disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving || aiLoading}
            >
              {saving ? 'Saving...' : (selectedTemplate && selectedTemplate.id ? 'Update Template' : 'Save Template')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 py-2.5 bg-white border-[1.5px] border-primary-blue text-primary-blue font-semibold rounded-lg hover:bg-primary-blue hover:text-white transition-colors text-base disabled:opacity-50"
              disabled={saving}
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

SimpleAddTemplateForm.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  selectedTemplate: PropTypes.object,
};

export default memo(SimpleAddTemplateForm);
